import { useMemo } from 'react';
import { atom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { createMutationAtom } from 'atoms/create-resource-atom';
import { getMapDefaultConfig } from 'components/map/atoms';
import type { MapUserConfig } from 'components/map/types';
import type { MapRef } from 'react-map-gl';
import type {
  PreDrillWorkspace,
  PreDrillWellboreDetails,
  PreDrillTargetZoneSuggestions,
  PreDrillVerificationItems,
  PreDrillVerificationItem,
  PreDrillVerificationEventResponse,
  PreDrillEnqueuedResponse,
  PreDrillStructureGridProfilesResponse,
  PreDrillGunBarrelResponse,
  PreDrillAgentRunsResponse,
  PreDrillSgcQueryResponse,
  PreDrillTargetZoneIntervalsResponse,
  WellsFacetGroup,
  WellMapResponse,
  WellborePointsResponse,
  IntersectedGridsResponse,
} from 'types/api-responses';

// ── Reads (GET resource atoms, keyed by hypermedia href) ────────────────────
// Thin show. `keep-old-data-on-refetch` avoids a spinner flash after a re-fetch.
export function usePreDrillWorkspaceResourceAtom(
  href: string | null | undefined
) {
  return useMemo(
    () =>
      createResourceAtom<PreDrillWorkspace>(
        href ?? undefined,
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}

// Wellbore Details card sub-resource.
export type WellboreDetailsAtom = ReturnType<typeof useWellboreDetailsAtom>;

export function useWellboreDetailsAtom(href: string | null | undefined) {
  return useMemo(
    () =>
      createResourceAtom<PreDrillWellboreDetails>(
        href ?? undefined,
        // Hold the card's values through the re-read that follows a geometry rebuild
        // so the derived fields update in place rather than flashing a loading state.
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}

// Story 7 — the derived wellbore points. `keep-old-data-on-refetch` so the table holds
// its rows through the re-read that follows an edit or a depth derivation.
export type WellborePointsAtom = ReturnType<typeof useWellborePointsAtom>;

export function useWellborePointsAtom(href: string | null | undefined) {
  return useMemo(
    () =>
      createResourceAtom<WellborePointsResponse>(
        href ?? undefined,
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}

// Story 8 — the structure grids the bore intersects, with the permitted depth placed
// against them. Re-read after any geometry change, since the ranges follow the vertices.
export function useIntersectedGridsAtom(href: string | null | undefined) {
  return useMemo(
    () =>
      createResourceAtom<IntersectedGridsResponse>(
        href ?? undefined,
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}

// Target Zone Identification sub-resource (candidates + agent details).
// `keep-old-data-on-refetch` so tiles don't flicker during a confirm/re-run.
export function useTargetZoneSuggestionsAtom(href: string | null | undefined) {
  return useMemo(
    () =>
      createResourceAtom<PreDrillTargetZoneSuggestions>(
        href ?? undefined,
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}

// Verification Tasks sub-resource (bare array). `keep-old-data-on-refetch` so
// the verdict (and the Mark-Correct toggle it drives) doesn't flicker.
export function useVerificationItemsAtom(href: string | null | undefined) {
  return useMemo(
    () =>
      createResourceAtom<PreDrillVerificationItems>(
        href ?? undefined,
        'keep-old-data-on-refetch'
      ),
    [href]
  );
}

// ── Writes (mutation atoms; snake_case payloads) ─────────────
// PATCH /wellbore_geometry_workspaces/:id — settings (name / SGC; cascades verdicts +
// marks geometry stale) and state-JSONB (color rules, divider, etc.).
export const updatePreDrillWorkspaceAtom = createMutationAtom<
  PreDrillWorkspace,
  'updatePreDrillWorkspace'
>();

// POST …/target_zone_confirmations — commits the zone; the server cascades +
// regenerates geometry, so onSuccess re-fetches the thin show.
export const confirmTargetZoneAtom = createMutationAtom<
  PreDrillEnqueuedResponse,
  'confirmPreDrillTargetZone'
>();

// POST …/geometry_refreshes — Recalculate Geometry (re-fetch show on success).
export const refreshGeometryAtom = createMutationAtom<
  PreDrillEnqueuedResponse,
  'refreshPreDrillGeometry'
>();

// Stories 7 and 8 write under /well_workspaces/:id/wellbores/:uwi14/…, each answering
// synchronously with the full points payload.

// POST …/geometry_edits — the whole ordered set; geometryStatus says if a path was built.
export const geometryEditAtom = createMutationAtom<
  WellborePointsResponse,
  'editWellboreGeometry'
>();

// PATCH …/points/:id — one point, applied and re-derived.
export const wellborePointUpdateAtom = createMutationAtom<
  WellborePointsResponse,
  'updateWellborePoint'
>();

// DELETE …/points/:id — same synchronous contract, renumbered from the surface down.
export const wellborePointDeleteAtom = createMutationAtom<
  WellborePointsResponse,
  'deleteWellborePoint'
>();

// POST …/target_zone_refreshes — Re-run agent / Get AI Suggestions (on demand).
export const targetZoneRefreshAtom = createMutationAtom<
  PreDrillEnqueuedResponse,
  'preDrillTargetZoneRefresh'
>();

// DELETE …/target_zone_suggestions — Clear Selection. Returns the neutral show,
// so onSuccess re-fetches (or the caller reuses the returned workspace).
export const clearTargetZoneAtom = createMutationAtom<
  PreDrillWorkspace,
  'clearPreDrillTargetZone'
>();

// POST /queries/pre_drill_target_zone_intervals — Change Match drawer catalog.
export const preDrillTargetZoneIntervalsAtom = createMutationAtom<
  PreDrillTargetZoneIntervalsResponse,
  'preDrillTargetZoneIntervalsQuery'
>();

// POST …/promotions — promote with verdict.
export const promotePreDrillWorkspaceAtom = createMutationAtom<
  PreDrillEnqueuedResponse,
  'promotePreDrillWorkspace'
>();

// POST /verification_items/:id/events — tri-state verdict (correct / incorrect
// / reverted) for a pre-drill verification item.
export const preDrillVerificationEventAtom = createMutationAtom<
  PreDrillVerificationEventResponse,
  'preDrillVerificationItemEvent'
>();

// PATCH /verification_items/:id — autosave the reviewer's note.
export const preDrillVerificationNoteAtom = createMutationAtom<
  PreDrillVerificationItem,
  'preDrillVerificationItemNote'
>();

// POST /queries/pre_drill_agent_runs — agent audit history for the subject UWI.
export const preDrillAgentRunsAtom = createMutationAtom<
  PreDrillAgentRunsResponse,
  'preDrillAgentRunsQuery'
>();

// POST /queries/pre_drill_agent_runs — inline Agent Output table. Separate from
// the Agent Runs panel atom so the two don't clobber in-flight state.
export const preDrillAgentOutputAtom = createMutationAtom<
  PreDrillAgentRunsResponse,
  'preDrillAgentRunsQuery'
>();

// POST /queries/structure_grid_collections — SGC candidates intersecting the
// subject (the picker drawer).
export const preDrillSgcQueryAtom = createMutationAtom<
  PreDrillSgcQueryResponse,
  'preDrillSgcQuery'
>();

// POST /queries/structure_grid_profiles — 2D Profile data (pre-drill subject).
export const preDrillStructureGridProfilesAtom = createMutationAtom<
  PreDrillStructureGridProfilesResponse,
  'preDrillStructureGridProfilesQuery'
>();

// POST /queries/gun_barrel — Gun Barrel cross-section (pre-drill subject).
export const preDrillGunBarrelAtom = createMutationAtom<
  PreDrillGunBarrelResponse,
  'preDrillGunBarrelQuery'
>();

// Well-map fetch for the Workspace Map (subject + nearby wellbore geometries).
export const preDrillWellMapAtom = createMutationAtom<
  WellMapResponse,
  'wellMap'
>();

// POST /queries/wells (profile2d variant) — subject well's surface→toe
// trajectory for the 2D Profile line. Separate from the map fetch.
export const preDrillWellProfileAtom = createMutationAtom<
  WellMapResponse,
  'wellMap'
>();

// Map layer-visibility config. This map opens with wheel zoom on and layer popups off for
// vertex editing (UAT 4, 12); both stay switchable, and no other map's preference moves.
const preDrillMapDefaults = (): MapUserConfig => {
  const config = getMapDefaultConfig();
  return {
    ...config,
    popups: { ...config.popups, checked: false },
    scrollZoom: { ...config.scrollZoom, checked: true },
  };
};
export const preDrillMapConfigAtom = atom<MapUserConfig>(preDrillMapDefaults());

// Dedicated ref to the Workspace Map (not the global `mapRefAtom`), so it
// always reflects this map's viewport — never a stale ref.
export const preDrillMapRefAtom = atom<MapRef | null>(null);

/** A–A′ on the geometry map: two [lon, lat] positions. View state only, never saved. */
export interface SectionLine {
  a: [number, number];
  b: [number, number];
}

// The map writes its A–A′ line here and the Explorer's cross-section reads it (UAT 10);
// an atom so a drag re-renders only the two subscribers.
export const preDrillSectionLineAtom = atom<SectionLine | null>(null);

/** A vertex of the map's working line, with the depths its point last read. */
export interface EditingPoint {
  lon: number;
  lat: number;
  tvdFt: number | null;
  tvdssFt: number | null;
}

// The map's working line while an edit is armed, null otherwise (UAT 35); the section
// previews where it crosses A–A′ ahead of the save.
export const preDrillEditingLineAtom = atom<EditingPoint[] | null>(null);

/** What the cross-section is pointing at, as feet along A–A′ (UAT 36). */
export interface SectionMarkers {
  /** Under the cursor on the section; null when nothing is hovered. */
  hoverFt: number | null;
  /** Where the section cuts the selected well; null when none is selected. */
  focusFt: number | null;
}

// The section writes what it points at here and the map marks the same place; the
// sheet's side of the bridge, since the section owns its own jotai store.
export const preDrillSectionMarkersAtom = atom<SectionMarkers>({
  hoverFt: null,
  focusFt: null,
});

// Color-by facet groups from the bbox-scoped `/queries/wells` facets call
// (fired on color-modal open); the modal reads its value lists here.
export const preDrillColorFacetsAtom = atom<Record<
  string,
  WellsFacetGroup | null
> | null>(null);

// Resolved color-by UWIs, keyed `${filterId}:${value}` → uwi[]; the map paints
// each bore by the matching rule's per-value color.
export const preDrillColorByUwiMapAtom = atom<Record<string, string[]>>({});
